import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  BudgetPlanStyles,
  StatusPill,
  UtilisationBar,
  formatDate,
  money,
  num,
} from "./BudgetPlanShared";

const Show = () => {
  const [receiveData, setReceiveData] = useState({});
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const res = await Http.get("/budget-plan/" + id);
        setReceiveData(res.data);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // The line rows carry budget vs. actuals: `consumed` is what journal
  // vouchers have booked against the line, so the gap is what is left to spend.
  const items = useMemo(
    () =>
      (receiveData.items || []).map((item) => ({
        ...item,
        account: item.chart_of_account
          ? item.chart_of_account.code + " - " + item.chart_of_account.name
          : "—",
      })),
    [receiveData]
  );

  const budgetTotal = num(receiveData.total);
  const consumedTotal = items.reduce((sum, item) => sum + num(item.consumed), 0);
  const remainingTotal = budgetTotal - consumedTotal;
  const overspent = remainingTotal < 0;
  const editable = receiveData.status && receiveData.status !== "approved";

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <BudgetPlanStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Budget Plan Details{" "}
              {receiveData.status && <StatusPill status={receiveData.status} />}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {receiveData.start_date
                ? `${formatDate(receiveData.start_date)} — ${formatDate(
                    receiveData.end_date
                  )}`
                : `Plan #${id}`}
            </p>
          </div>
          <div className="d-flex gap-2 bp-no-print">
            <Link to="/budget-plan" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => window.print()}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
            {editable && (
              <Link
                to={`/edit-budget-plan/${id}`}
                className="btn btn-primary"
              >
                <i className="fa fa-pencil mg-r-6"></i>Edit
              </Link>
            )}
          </div>
        </div>

        <div className="am-pagebody">
          {loading ? (
            <div className="bp-panel">
              <div className="bp-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : !receiveData.id ? (
            <div className="bp-panel">
              <div className="bp-empty">
                <i className="fa fa-folder-open-o"></i>
                <h6>No budget plan found</h6>
                <p className="mb-0 tx-12">
                  Plan #{id} does not exist or has been deleted.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="row mg-b-20">
                <div className="col-md-3 mg-b-10">
                  <div className="bp-stat">
                    <i className="fa fa-briefcase bp-stat-icon"></i>
                    <span className="bp-stat-label">Total Budget</span>
                    <span className="bp-stat-value">{money(budgetTotal)}</span>
                    <span className="bp-stat-sub">
                      Across {items.length}{" "}
                      {items.length === 1 ? "line" : "lines"}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div className="bp-stat is-pending">
                    <i className="fa fa-arrow-circle-o-down bp-stat-icon"></i>
                    <span className="bp-stat-label">Spent</span>
                    <span className="bp-stat-value">
                      {money(consumedTotal)}
                    </span>
                    <span className="bp-stat-sub">
                      Booked by journal vouchers
                    </span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div
                    className={`bp-stat ${overspent ? "is-alert" : "is-ok"}`}
                  >
                    <i className="fa fa-balance-scale bp-stat-icon"></i>
                    <span className="bp-stat-label">
                      {overspent ? "Overspent By" : "Remaining"}
                    </span>
                    <span className="bp-stat-value">
                      {money(Math.abs(remainingTotal))}
                    </span>
                    <span className="bp-stat-sub">
                      {overspent
                        ? "Spend exceeds the approved budget"
                        : "Still available to book"}
                    </span>
                  </div>
                </div>
                <div className="col-md-3 mg-b-10">
                  <div className="bp-stat">
                    <i className="fa fa-pie-chart bp-stat-icon"></i>
                    <span className="bp-stat-label">Utilisation</span>
                    <span className="bp-stat-value">
                      {budgetTotal > 0
                        ? `${((consumedTotal / budgetTotal) * 100).toFixed(1)}%`
                        : "—"}
                    </span>
                    <div className="mg-t-6">
                      <UtilisationBar
                        budget={budgetTotal}
                        spent={consumedTotal}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-4">
                  <div className="bp-panel">
                    <div className="bp-panel-head">
                      <span>
                        <i className="fa fa-info-circle me-2"></i>Plan
                        Information
                      </span>
                    </div>
                    <div className="bp-panel-body pd-t-6">
                      <div className="bp-kv">
                        <span className="bp-kv-label">Plan ID</span>
                        <span className="bp-kv-value">#{receiveData.id}</span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">Start Date</span>
                        <span className="bp-kv-value">
                          {formatDate(receiveData.start_date)}
                        </span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">End Date</span>
                        <span className="bp-kv-value">
                          {formatDate(receiveData.end_date)}
                        </span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">Remarks</span>
                        <span className="bp-kv-value">
                          {receiveData.message || "—"}
                        </span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">Created By</span>
                        <span className="bp-kv-value">
                          {receiveData.created_by?.name || "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bp-panel">
                    <div className="bp-panel-head">
                      <span>
                        <i className="fa fa-check-square-o me-2"></i>Approval
                      </span>
                    </div>
                    <div className="bp-panel-body pd-t-6">
                      <div className="bp-kv">
                        <span className="bp-kv-label">Status</span>
                        <span className="bp-kv-value">
                          <StatusPill status={receiveData.status} />
                        </span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">Approved By</span>
                        <span className="bp-kv-value">
                          {receiveData.approved_by?.name || "—"}
                        </span>
                      </div>
                      <div className="bp-kv">
                        <span className="bp-kv-label">Approved At</span>
                        <span className="bp-kv-value">
                          {formatDate(receiveData.approved_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bp-panel">
                    <div className="bp-panel-head">
                      <span>
                        <i className="fa fa-calculator me-2"></i>Summary
                      </span>
                    </div>
                    <div className="bp-panel-body">
                      <div className="bp-total-box">
                        <div className="bp-total-row">
                          <span>Total budget</span>
                          <span>{money(budgetTotal)}</span>
                        </div>
                        <div className="bp-total-row">
                          <span>Less: spent</span>
                          <span>{money(consumedTotal)}</span>
                        </div>
                        <div className="bp-total-row bp-total-net">
                          <span>
                            {overspent ? "Overspent by" : "Remaining"}
                          </span>
                          <span>{money(Math.abs(remainingTotal))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-8">
                  <div className="bp-panel">
                    <div className="bp-panel-head">
                      <span>
                        <i className="fa fa-list-ul me-2"></i>Budget Lines
                      </span>
                      <span className="bp-head-note">
                        Budget against what journal vouchers have booked
                      </span>
                    </div>
                    <div className="table-responsive">
                      <table className="table bp-line-table">
                        <thead>
                          <tr>
                            <th style={{ width: "44px" }}>#</th>
                            <th>Chart of Account</th>
                            <th>Purpose</th>
                            <th className="bp-num">Budget</th>
                            <th className="bp-num">Spent</th>
                            <th className="bp-num">Remaining</th>
                            <th style={{ width: "140px" }}>Utilisation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.length === 0 && (
                            <tr>
                              <td colSpan="7">
                                <div className="bp-empty">
                                  <i className="fa fa-inbox"></i>
                                  <span>
                                    This plan has no budget lines yet.
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {items.map((item, index) => {
                            const remaining = num(item.remaining);
                            const isOver = remaining < 0;
                            return (
                              <tr key={item.id || index}>
                                <td>
                                  <span className="bp-row-index">
                                    {index + 1}
                                  </span>
                                </td>
                                <td style={{ fontWeight: 600, color: "#2a2962" }}>
                                  {item.account}
                                </td>
                                <td>{item.purpose || "—"}</td>
                                <td className="bp-num">{money(item.amount)}</td>
                                <td className="bp-num">
                                  {money(item.consumed)}
                                </td>
                                <td
                                  className="bp-num"
                                  style={{
                                    fontWeight: 600,
                                    color: isOver ? "#e42320" : "#1f9d55",
                                  }}
                                >
                                  {money(remaining)}
                                </td>
                                <td>
                                  <UtilisationBar
                                    budget={item.amount}
                                    spent={item.consumed}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {items.length > 0 && (
                          <tfoot>
                            <tr>
                              <th colSpan="3" className="bp-num">
                                Total
                              </th>
                              <th className="bp-num">{money(budgetTotal)}</th>
                              <th className="bp-num">{money(consumedTotal)}</th>
                              <th
                                className="bp-num"
                                style={{ color: overspent ? "#e42320" : "#2a2962" }}
                              >
                                {money(remainingTotal)}
                              </th>
                              <th></th>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Show;
